import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AppConfigProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UploadProvider } from "@/context/UploadContext";
import { RecorderProvider } from "@/context/RecorderContext";
import { AppLayout } from "@/components/app/AppLayout";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordPages";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Meetings from "@/pages/Meetings";
import RecordPage from "@/pages/RecordPage";
import UploadPage from "@/pages/UploadPage";
import BotPage from "@/pages/BotPage";
import MeetingDetail from "@/pages/MeetingDetail";
import Files from "@/pages/Files";
import Workspace from "@/pages/Workspace";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Contact from "@/pages/Contact";
import Support from "@/pages/Support";
import PaymentResult from "@/pages/PaymentResult";
import Legal from "@/pages/Legal";
import SharedMeeting from "@/pages/SharedMeeting";
import { AuthCallback } from "@/components/GoogleAuth";
import { VerifyEmail, VerifyPending } from "@/pages/VerifyEmail";
import JoinWorkspace from "@/pages/JoinWorkspace";

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null)
    return (
      <div className="grid min-h-screen place-items-center" data-testid="auth-loading">
        <Loader2 className="animate-spin text-violet-600" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (user.email_verified === false) return <VerifyPending />;
  if (!user.onboarded && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (admin && !user.is_super_admin) return <Navigate to="/app" replace />;
  return loc.pathname === "/onboarding" ? children : <AppLayout>{children}</AppLayout>;
}

const P = (el, admin) => <Protected admin={admin}>{el}</Protected>;

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/join/:token" element={<JoinWorkspace />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/share/:token" element={<SharedMeeting />} />
      <Route path="/onboarding" element={P(<Onboarding />)} />
      <Route path="/app" element={P(<Dashboard />)} />
      <Route path="/app/meetings" element={P(<Meetings />)} />
      <Route path="/app/record" element={P(<RecordPage />)} />
      <Route path="/app/upload" element={P(<UploadPage />)} />
      <Route path="/app/bot" element={P(<BotPage />)} />
      <Route path="/app/meetings/:id" element={P(<MeetingDetail />)} />
      <Route path="/app/actions" element={<Navigate to="/app" replace />} />
      <Route path="/app/assistant" element={<Navigate to="/app" replace />} />
      <Route path="/app/files" element={P(<Files />)} />
      <Route path="/app/workspace" element={P(<Workspace />)} />
      <Route path="/app/integrations" element={<Navigate to="/app" replace />} />
      <Route path="/app/billing" element={P(<Billing />)} />
      <Route path="/app/settings" element={P(<Settings />)} />
      <Route path="/payment/success" element={P(<PaymentResult />)} />
      <Route path="/payment/cancel" element={P(<PaymentResult cancelled />)} />
      <Route path="/app/support" element={P(<Support />)} />
      <Route path="/app/admin" element={P(<Admin />, true)} />
      <Route path="/app/*" element={<Navigate to="/app" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppConfigProvider>
      <AuthProvider>
        <BrowserRouter>
          <UploadProvider>
            <RecorderProvider>
              <AppRoutes />
            </RecorderProvider>
          </UploadProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </AppConfigProvider>
  );
}

export default App;
